import { isDefined } from './generics';
import { memoize2, memoize3 } from './memoize';

export const compact = <T>(xs: (T | undefined | null)[]): T[] => {
  return xs.filter((x): x is T => x !== null && x !== undefined);
};

export const mapKeyBy = <K, V>(
  xs: readonly V[],
  fn: (x: V, i: number) => K,
): Map<K, V> => {
  const res = new Map<K, V>();
  let i = 0;
  for (const x of xs) {
    res.set(fn(x, i++), x);
  }
  return res;
};

export const mapIterator = <In, Out>(
  xs: Iterable<In>,
  fn: (x: NonNullable<In>) => Out,
): Out[] => {
  const out: Out[] = [];
  for (const x of xs) {
    if (x) {
      out.push(fn(x));
    }
  }
  return out;
};

export const mapKeyByM = memoize2(mapKeyBy);

export const mapKeyBy2 = <K1, K2, V>(
  xs: ReadonlyArray<V>,
  fn1: (x: V) => K1,
  fn2: (x: V) => K2,
): DefaultMap<K1, Map<K2, V>> => {
  const res = new DefaultMap<K1, Map<K2, V>>(() => new Map());

  for (const x of xs) {
    res.get(fn1(x)).set(fn2(x), x);
  }
  return res;
};

export const mapKeyBy2M = memoize3(mapKeyBy2);

export const mapKeyBy3 = <K1, K2, K3, V>(
  xs: ReadonlyArray<V>,
  fn1: (x: V) => K1,
  fn2: (x: V) => K2,
  fn3: (x: V) => K3,
): DefaultMap<K1, DefaultMap<K2, Map<K3, V>>> => {
  const res = new DefaultMap<K1, DefaultMap<K2, Map<K3, V>>>(
    () => new DefaultMap<K2, Map<K3, V>>(() => new Map<K3, V>()),
  );

  for (const x of xs) {
    res.get(fn1(x)).get(fn2(x)).set(fn3(x), x);
  }

  return res;
};

export const mapKeyBy4 = <K1, K2, K3, K4, V>(
  xs: ReadonlyArray<V>,
  fn1: (x: V) => K1,
  fn2: (x: V) => K2,
  fn3: (x: V) => K3,
  fn4: (x: V) => K4,
): Map4<K1, K2, K3, K4, V> => {
  const res = new DefaultMap<K1, DefaultMap<K2, DefaultMap<K3, Map<K4, V>>>>(
    () =>
      new DefaultMap<K2, DefaultMap<K3, Map<K4, V>>>(
        () => new DefaultMap<K3, Map<K4, V>>(() => new Map()),
      ),
  );

  for (const x of xs) {
    res.get(fn1(x)).get(fn2(x)).get(fn3(x)).set(fn4(x), x);
  }

  return res;
};

/** Key items by a function into a Map. The function should return an array of keys and the item will be keyed under all of them.
 * If the same key is used multiple times, the last item */
export const mapKeyByMany = <K, V>(
  xs: V[],
  fn: (x: V, i: number) => K[],
): Map<K, V> => {
  const res = new Map<K, V>();
  let i = 0;
  for (const x of xs) {
    const keys = fn(x, i++);
    for (const key of keys) {
      res.set(key, x);
    }
  }
  return res;
};

export const mapById = <V extends { id: number }>(
  xs: undefined | Array<V | undefined>,
): Map<number, V> => {
  return mapKeyBy(xs?.filter(isDefined) ?? [], (x) => x.id);
};

/** Group items by a function into a Map. Identical semantic to typical groupBy except the result is a Map, not Record.
 * Stable; the items in result arrays preserve the order they were in the original.
 */
export const mapGroupBy = <K, V>(
  xs: ReadonlyArray<V>,
  fn: (x: NoInfer<V>, i: number) => K,
): Map<K, V[]> => {
  const res = new Map<K, V[]>();
  let i = 0;
  for (const x of xs) {
    const key = fn(x, i++);
    let value = res.get(key);
    if (value == undefined) {
      value = [];
      res.set(key, value);
    }
    value.push(x);
  }
  return res;
};

export const mapGroupBy2 = <K1, K2, V>(
  xs: ReadonlyArray<V>,
  fn1: (x: NoInfer<V>) => K1,
  fn2: (x: NoInfer<V>) => K2,
) => {
  const res = new DefaultMap<K1, DefaultMap<K2, V[]>>(
    () => new DefaultMap(() => []),
  );

  for (const x of xs) {
    res.get(fn1(x)).get(fn2(x)).push(x);
  }

  return res;
};

export const mapGroupBy3 = <K1, K2, K3, V>(
  xs: ReadonlyArray<V>,
  fn1: (x: NoInfer<V>) => K1,
  fn2: (x: NoInfer<V>) => K2,
  fn3: (x: NoInfer<V>) => K3,
) => {
  const res = new DefaultMap<K1, DefaultMap<K2, DefaultMap<K3, V[]>>>(
    () => new DefaultMap(() => new DefaultMap(() => [])),
  );

  for (const x of xs) {
    res.get(fn1(x)).get(fn2(x)).get(fn3(x)).push(x);
  }

  return res;
};

export type Map4<K1, K2, K3, K4, V> = DefaultMap<
  K1,
  DefaultMap<K2, DefaultMap<K3, Map<K4, V>>>
>;

/** Group items by a function into a Map. The function should return an array of keys and the item will be grouped under all of them. */
export const mapGroupByMany = <K, V>(
  xs: V[],
  fn: (x: NoInfer<V>, i: number) => K[],
): Map<K, V[]> => {
  const res = new Map<K, V[]>();
  let i = 0;
  for (const x of xs) {
    const keys = fn(x, i++);
    for (const key of keys) {
      let value = res.get(key);
      if (value == undefined) {
        value = [];
        res.set(key, value);
      }
      value.push(x);
    }
  }
  return res;
};

export const mapEvery = <K, V>(
  map: Map<K, V>,
  fn: (v: V, k: K) => boolean,
): boolean => {
  for (const [k, v] of map.entries()) {
    if (!fn(v, k)) {
      return false;
    }
  }
  return true;
};

export const reverseMap = <K, V>(map: Map<K, V>): Map<V, K> => {
  const res = new Map<V, K>();
  for (const [k, v] of map.entries()) {
    res.set(v, k);
  }
  return res;
};

// A Map which generates a default value on .get if the key doesn't already exist
export class DefaultMap<K, T> extends Map<K, T> {
  constructor(private builder: (key: K) => T, init?: [K, T][] | Map<K, T>) {
    super(init);
  }

  override get(key: K): T {
    const existing = super.get(key);
    if (existing) {
      return existing;
    } else {
      const item = this.builder(key);
      this.set(key, item);
      return item;
    }
  }

  update(key: K, updater: (oldValue: T | undefined) => T): void {
    this.set(key, updater(this.get(key)));
  }
}

/** Merges objects key by key by using the merging functions provided. Each key specifies its own merging function. */
export function objectsMergeWith<T>(
  objects: T[],
  mergers: {
    [K in keyof T]: (items: T[K][]) => T[K];
  },
): T {
  const result: Partial<T> = {};
  for (const key in objects[0]) {
    result[key] = mergers[key](objects.map((o) => o[key]));
  }
  return result as T;
}

/** Merge map entries by key using the provided merger function */
export function mapsMergeWith<K, V>(merger: (items: V[]) => V) {
  return (maps: Map<K, V>[]) => {
    const result = new Map<K, V>();
    const allKeys = new Set<K>(maps.flatMap((m) => [...m.keys()]));

    for (const key of allKeys) {
      result.set(key, merger(compact(maps.map((m) => m.get(key)))));
    }
    return result;
  };
}

/** Returns the first entry of a Map */
export function mapGetFirstEntry<KeyType, ValueType>(
  map: Map<KeyType, ValueType>,
): [KeyType, ValueType] | undefined {
  if (map.size === 0) return undefined;
  return map.entries().next().value;
}

/** Returns the first value of a Set */
export function setGetFirstValue<ValueType>(
  set: Set<ValueType>,
): ValueType | undefined {
  if (set.size === 0) return undefined;
  return set.values().next().value;
}

/** Returns the last value of a Set */
export function setGetLastValue<ValueType>(
  set: Set<ValueType>,
): ValueType | undefined {
  if (set.size === 0) return undefined;
  return Array.from(set).at(-1);
}

/** Find the item in the array that has the minimum value of the function.
 * Same as lodash.minBy but faster. */
export function findMinBy<T>(
  xs: readonly T[],
  fn: (x: T) => number,
): T | undefined {
  if (xs.length === 0) return undefined;
  let best = Infinity;
  let bestItem: T | undefined;
  for (const x of xs) {
    const y = fn(x);
    if (y < best) {
      best = y;
      bestItem = x;
    }
  }
  return bestItem;
}

/** Find the item in the array that has the maximum value of the function.
 * Same as lodash.maxBy but faster. */
export function findMaxBy<T>(
  xs: readonly T[],
  fn: (x: T) => number,
): T | undefined {
  if (xs.length === 0) return undefined;
  let best = -Infinity;
  let bestItem: T | undefined;
  for (const x of xs) {
    const y = fn(x);
    if (y > best) {
      best = y;
      bestItem = x;
    }
  }
  return bestItem;
}

/** Get the sum of a function applied to all items in an array.
 * Same as lodash.sumBy but faster. */
export function getSumBy<T>(xs: T[], fn: (x: T) => number): number {
  return xs.reduce((acc, x) => acc + fn(x), 0);
}
